import {GradeScaleRI} from '../rest-interfaces/grade-scale-ri';
import {AssignmentScoreType} from '../rest-interfaces/assignment/assignment-score-type';
import {ScoreRI} from '../rest-interfaces/score-ri';
import {NumberUtil} from './number-util';
import roundToPrecision = NumberUtil.roundToPrecision;
import {GradeScaleUtil} from './grade-scale-util';
import {GradeScaleItemRI} from '../rest-interfaces/grade-scale-item-ri';

const PRECISION = 6;
const PLUS_MINUS_REGEX = /[+\-]$/;

export namespace ScoreUtil {

  import CalcRelationship = GradeScaleUtil.CalcRelationship;

  /* Percent Value Determination to assign to Standard Score for Push Calculation as per AC's discussion with Danny and Jeff.  These have been blessed by Renee and Pamela.

    Assignment Score Type -> Standard Score Type

    Rule 1:
        POINTS -> POINTS
        POINTS -> PERCENT
        POINTS -> GRADESCALE
        - Always use the % calculated from the assignment points

    Rule 2:
        PERCENT -> POINTS
        PERCENT -> PERCENT
        PERCENT -> GRADESCALE
        - Always use the assignment %

    Rule 3:
        GRADESCALE -> POINTS
        GRADESCALE -> PERCENT
        GRADESCALE -> GRADESCALE
        - Always use the assignment % calculated based on the assignment grade scale
  */
  export function pushCalculation(fromScore: ScoreRI,
                                 fromGradeScale: GradeScaleRI,
                                 fromScoreType: AssignmentScoreType,
                                 fromMaxPoints: number,
                                 toGradeScale: GradeScaleRI,
                                 toScoreType: AssignmentScoreType,
                                 toMaxPoints: number,
                                 toScore?: ScoreRI): ScoreRI {
    if (toScore == null) {
      toScore = {};
    }

    // * * * * * * * * * * * * * * * * *       Populate Typed Score First       * * * * * * * * * * * * * * * * * *
    //Easiest case first.
    if (toGradeScale.uid === fromGradeScale.uid &&
      toScoreType === fromScoreType &&
      toMaxPoints === fromMaxPoints) {
      //Everything is the same! Just copy it over!
      copyFullScore(fromScore, toScore);
    }

    switch (fromScoreType) {
      case 'POINTS':
        pushPoints(fromScore,
          fromGradeScale,
          fromMaxPoints,
          toGradeScale,
          toScoreType,
          toMaxPoints,
          toScore);
        break;
      case 'PERCENT':
        pushPercent(fromScore,
          fromGradeScale,
          toGradeScale,
          toScoreType,
          toMaxPoints,
          toScore);
        break;
      case 'GRADESCALE':
        pushGradeScale(fromScore,
          fromGradeScale,
          fromMaxPoints,
          toGradeScale,
          toScoreType,
          toMaxPoints,
          toScore);
        break;
      default:
        throw 'do not call this function on something without a scoretype';
    }

    // For cases when we need to use the percentage coming from the "to" grade scale (condition where the toScore.percent is null), this function will compute this percent.
    let calculatePercentForGradeScaleIfMissing = (score: ScoreRI, scoreType: AssignmentScoreType, gradeScale: GradeScaleRI): void => {
      if (score != null && score.percent == null) {
        // Alwyas compute the percent when converting to score type of GRADESCALE.  This is required in order for the standards progress to get updated in SIS.
        let grade = getGrade(score, gradeScale);
        //Push does not support 'LEVELS' calcRelationship
        let percentAsString: string | undefined = GradeScaleUtil.convertGradeScaleItemToScoreOfType(grade, gradeScale, 'PERCENT', false, 'GRADESCALE');
        if (percentAsString != null) {
          score.percent = Number(percentAsString);
        }
      }
    };

    // * * * * * * * * * * * * * * * * *       Populate GradeScale Score Second       * * * * * * * * * * * * * * * * * *
    let gsi: string | undefined;

    if (fromGradeScale.gradeScaleType === toGradeScale.gradeScaleType) {
      if (fromGradeScale.gradeScaleType === 'ALPHA') {
        if (fromGradeScale.uid === toGradeScale.uid) {
          let gs: GradeScaleItemRI | undefined = GradeScaleUtil.getGradeScaleItem(fromScore.letterGrade, fromGradeScale);
          if (gs != null) {
            toScore.letterGrade = fromScore.letterGrade;
            calculatePercentForGradeScaleIfMissing(toScore, toScoreType, toGradeScale);
            delete toScore.numericGrade;
            return toScore;
          }
        }
      } else if (fromGradeScale.gradeScaleType === 'NUMERIC') {
        if (fromGradeScale.numericMax === toGradeScale.numericMax &&
          fromGradeScale.numericMin === toGradeScale.numericMin) {
          //They are similar grade scales.
          numericGradeScaleToNumericGradeScale(fromScore, fromGradeScale, toScore, toGradeScale);
          calculatePercentForGradeScaleIfMissing(toScore, toScoreType, toGradeScale);
          return toScore;
        }
      }
    }

    //This is populating the gradeScale Score VIA conversion of the typed score. (2 hops)
    switch (toScoreType) {
      case 'POINTS':
        gsi = GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(toScore.points),
          toGradeScale,
          'POINTS',
          false,
          'GRADESCALE',
          toMaxPoints);
        setGrade(gsi, toScore, toGradeScale);
        break;
      case 'PERCENT':
        gsi = GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(toScore.percent),
          toGradeScale,
          'PERCENT',
          false,
          'GRADESCALE',
          0);
        setGrade(gsi, toScore, toGradeScale);
        break;
      case 'GRADESCALE':
        calculatePercentForGradeScaleIfMissing(toScore, toScoreType, toGradeScale);
        break;
      default: //shut up tslint
    }

    if (toScore.points != null) {
      toScore.points = NumberUtil.round(toScore.points, 2);
    }

    if (toScore.percent != null) {
      toScore.percent = NumberUtil.round(toScore.percent, 2);
    }

    let numberOfDecimals: number = toGradeScale.numericDecimals ? toGradeScale.numericDecimals : 2;
    if (toScore.letterGrade != null && !isNaN(<any>toScore.letterGrade)) {
      toScore.letterGrade = String(NumberUtil.round(Number(toScore.letterGrade), numberOfDecimals));
    }

    if (toScore.numericGrade != null) {
      toScore.numericGrade = NumberUtil.round(toScore.numericGrade, numberOfDecimals);
    }

    return toScore;
  }

  export function pushPoints(fromScore: ScoreRI,
                            fromGradeScale: GradeScaleRI,
                            fromMaxPoints: number,
                            toGradeScale: GradeScaleRI,
                            toScoreType: AssignmentScoreType,
                            toMaxPoints: number,
                            toScore: ScoreRI,
                            roundNumber = true): void {

    let calcPercent = (points = 0, maxPoints: number): number => {
      return roundToPrecision(points / maxPoints * 100, 2, PRECISION, roundNumber);
    };

    switch (toScoreType) {
      case 'POINTS':
        if (fromMaxPoints === toMaxPoints) {
          toScore.points = fromScore.points;
        } else {
          toScore.points = roundToPrecision(fromScore.points / fromMaxPoints * toMaxPoints, 2, PRECISION, roundNumber);
        }
        // As per AC's conversation with Danny, use the percent calculated from points as the percent to use for toScore.percent.
        toScore.percent = calcPercent(fromScore.points, fromMaxPoints);
        break;
      case 'PERCENT':
        toScore.percent = calcPercent(fromScore.points, fromMaxPoints);
        break;
      case 'GRADESCALE':
        //Push does not support 'LEVELS' calcRelationship
        toScore.percent = calcPercent(fromScore.points, fromMaxPoints);
        setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(fromScore.points), toGradeScale, 'POINTS', true, 'GRADESCALE', fromMaxPoints),
          toScore, toGradeScale);
        break;
      default: //no-op
    }
  }

  export function pushPercent(fromScore: ScoreRI,
                             fromGradeScale: GradeScaleRI,
                             toGradeScale: GradeScaleRI,
                             toScoreType: AssignmentScoreType,
                             toMaxPoints: number,
                             toScore: ScoreRI,
                             roundNumber = true): void {
    switch (toScoreType) {
      case 'POINTS':
        toScore.points = roundToPrecision(fromScore.percent / 100 * toMaxPoints, 2, PRECISION, roundNumber);
        // As per AC's conversation with Danny, for percent, we just want to use the fromScore.percent value as the toScore.percent
        toScore.percent = fromScore.percent;
        break;
      case 'PERCENT':
        toScore.percent = fromScore.percent;
        break;
      case 'GRADESCALE':
        //Push does not support 'LEVELS' calcRelationship
        // As per AC's conversation with Danny, for percent, we just want to use the fromScore.percent value as the toScore.percent
        toScore.percent = fromScore.percent;
        setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(fromScore.percent), toGradeScale, 'PERCENT', true, 'GRADESCALE'),
          toScore,
          toGradeScale);
        break;
      default: //no-op
    }
  }

  export function pushGradeScale(fromScore: ScoreRI,
                                fromGradeScale: GradeScaleRI,
                                fromMaxPoints: number,
                                toGradeScale: GradeScaleRI,
                                toScoreType: AssignmentScoreType,
                                toMaxPoints: number,
                                toScore: ScoreRI,
                                roundNumber = true): void {
    switch (toScoreType) {
      case 'POINTS':
        let points: string | undefined = GradeScaleUtil.convertGradeScaleItemToScoreOfType(getGrade(fromScore, fromGradeScale),
          fromGradeScale,
          'POINTS',
          true,
          'GRADESCALE',
          toMaxPoints);
        let fromPoints: number = points != null ? Number(points) : 0;
        toScore.points = fromPoints;
        // As per AC's conversation with Danny, use the percent calculated from points as the percent to use for toScore.percent.
        toScore.percent = roundToPrecision(fromPoints / fromMaxPoints * 100, 2, PRECISION, roundNumber);
        break;
      case 'PERCENT':
        let percent: string | undefined = GradeScaleUtil.convertGradeScaleItemToScoreOfType(getGrade(fromScore, fromGradeScale),
          fromGradeScale,
          'PERCENT',
          true,
          'GRADESCALE',
          toMaxPoints);
        toScore.percent = percent != null ? Number(percent) : undefined;
        break;
      case 'GRADESCALE':
        gradeScaleToGradeScale(fromScore, fromGradeScale, toScore, toGradeScale);
        break;
      default: //no op
    }
  }

  export function gradeScaleToGradeScale(fromScore: ScoreRI,
                                        fromGradeScale: GradeScaleRI,
                                        toScore: ScoreRI,
                                        toGradeScale: GradeScaleRI): void {
    if (fromGradeScale.uid === toGradeScale.uid) {
      toScore.letterGrade = fromScore.letterGrade;
      toScore.numericGrade = fromScore.numericGrade;
      return;
    }

    if (fromGradeScale.gradeScaleType === 'NUMERIC') {
      if (toGradeScale.gradeScaleType === 'NUMERIC') {
        // NUMERIC ---> NUMERIC

        numericGradeScaleToNumericGradeScale(fromScore, fromGradeScale, toScore, toGradeScale);

      } else {
        // NUMERIC ---> ALPHA
        let fromGrade = getGrade(fromScore, fromGradeScale);
        let fromPercent: number | undefined = GradeScaleUtil.convertNumericGradeScaleItemToPercent(fromGrade, fromGradeScale, false);
        setGrade(GradeScaleUtil.convertPercentToGradeScaleItem(fromPercent, toGradeScale), toScore, toGradeScale);

        // As per discussion with Danny, Jeff and AC, this is what we concluded this should be.
        toScore.percent = fromPercent;
      }
    } else {
      // ALPHA ---> NUMERIC
      // ALPHA ---> ALPHA
      let percent: number | undefined = GradeScaleUtil.convertNumericGradeScaleItemToPercent(fromScore.letterGrade, fromGradeScale, false);

      // As per discussion with Danny, Jeff and AC, this is what we concluded this should be.
      toScore.percent = percent;

      setGrade(GradeScaleUtil.convertPercentToGradeScaleItem(percent, toGradeScale), toScore, toGradeScale);
    }
  }

  export function numericGradeScaleToNumericGradeScale(fromScore: ScoreRI,
                                                      fromGradeScale: GradeScaleRI,
                                                      toScore: ScoreRI,
                                                      toGradeScale: GradeScaleRI): void {
    let gradeScaleScore = getGrade(fromScore, fromGradeScale);

    if (fromGradeScale.numericMin === toGradeScale.numericMin && fromGradeScale.numericMax === toGradeScale.numericMax) {
      //Searching the toGradeScale for the fromScore.
      let foundGsi: GradeScaleItemRI | undefined = GradeScaleUtil.getGradeScaleItem(gradeScaleScore, toGradeScale);
      if (foundGsi != null) {
        setGrade(gradeScaleScore, toScore, toGradeScale);
      } else {
        let fromNumericValue: number | undefined = GradeScaleUtil.convertNumericGradeScaleItemToNumericValue(gradeScaleScore, fromGradeScale);
        if (fromNumericValue == null) {
          delete toScore.letterGrade;
          delete toScore.numericGrade;
          return;
        }

        if (toGradeScale.conversionToNumeric === 'PERCENT' || hasNumericDecimals(toGradeScale)) {
          setGrade(String(fromNumericValue), toScore, toGradeScale);
        } else {
          setGrade(String(GradeScaleUtil.convertNumericValueTo('GRADESCALE', fromNumericValue, toGradeScale, 'GRADESCALE', true)), toScore, toGradeScale);
        }
      }

      if (toScore.percent == null) {
        // As per discussion with Danny, Jeff and AC, this is what we concluded this should be.
        let fromPercent: number | undefined = GradeScaleUtil.convertNumericGradeScaleItemToPercent(<string>gradeScaleScore, fromGradeScale, false);
        toScore.percent = fromPercent;
      }
    } else {
      //The numeric grade scales aren't really compatible.
      let fromPercent: number | undefined = GradeScaleUtil.convertNumericGradeScaleItemToPercent(<string>gradeScaleScore, fromGradeScale, false);
      if (toScore.percent == null) {
        toScore.percent = fromPercent;
      }
      setGrade(GradeScaleUtil.convertPercentToNumericGradeScaleItem(fromPercent, toGradeScale, true), toScore, toGradeScale);
    }
  }

  /**
   * Just copies all values from fromScore to toScore.
   */
  export function copyFullScore(fromScore: ScoreRI, toScore: ScoreRI): void {
    toScore.actualScoreEntered = fromScore.actualScoreEntered;
    toScore.letterGrade = fromScore.letterGrade;
    toScore.numericGrade = fromScore.numericGrade;
    toScore.percent = fromScore.percent;
    toScore.points = fromScore.points;
  }

  export function setGrade(gradeScaleScore: string | number | undefined, score: ScoreRI, gradeScale): ScoreRI {
    if (score == null) {
      return {};
    }

    if (gradeScale == null) {
      return score;
    }

    if (gradeScaleScore == null) {
      delete score.numericGrade;
      delete score.letterGrade;
      return score;
    }

    if (gradeScale.gradeScaleType === 'ALPHA') {
      score.letterGrade = String(gradeScaleScore);
      delete score.numericGrade;
      return score;
    }

    if (gradeScale.gradeScaleType === 'NUMERIC') {

      let numericWithExtras: boolean = gradeScale.numericExtras != null && gradeScale.numericExtras !== 'NONE';
      if (numericWithExtras) {
        score.letterGrade = String(gradeScaleScore);
      }

      if (!isNaN(<any>gradeScaleScore)) {
        score.numericGrade = Number(gradeScaleScore);
      } else {
        if (gradeScaleScore != null) {
          gradeScaleScore = String(gradeScaleScore);
        }
        let gradeScaleItem: GradeScaleItemRI | undefined = GradeScaleUtil.getNumericGradeScaleItem(<string | undefined>gradeScaleScore, gradeScale);
        if (gradeScaleItem != null) {
          score.numericGrade = gradeScaleItem.numericValue;
        } else {
          delete score.numericGrade;
        }
      }
    }

    return score;
  }

  export function getGrade(score: ScoreRI, gradeScale: GradeScaleRI): string | undefined {
    if (gradeScale == null || score == null) {
      return;
    }

    if (gradeScale.gradeScaleType === 'ALPHA') {
      return score.letterGrade;
    }
    let numericWithExtras: boolean = gradeScale.numericExtras != null && gradeScale.numericExtras !== 'NONE';
    if (numericWithExtras) {
      return score.letterGrade;
    }

    if (score.numericGrade != null) {
      return String(score.numericGrade);
    }
  }

  export function rollUpCalculation(fromScores: Array<{fromScore: ScoreRI,
                                      fromGradeScale: GradeScaleRI,
                                      fromScoreType: AssignmentScoreType,
                                      fromMaxPoints: number
                                      fromWeight?: number}>,
                                    calcRelationship: CalcRelationship,
                                    toGradeScale: GradeScaleRI,
                                    toScoreType: AssignmentScoreType,
                                    toMaxPoints?: number,
                                    toScore?: ScoreRI): ScoreRI {

    if (toScore == null) {
      toScore = {};
    }

    //Figure out if all of the child grade scales are numeric AND have similar grade scales. (same max and min)
    let types = {};
    let numericSimilar: { max: number, min: number } | undefined | false;
    for (let fromScoreInfo of fromScores) {
      types[fromScoreInfo.fromScoreType] = true;

      if (numericSimilar !== false) {
        if (fromScoreInfo.fromScoreType === 'GRADESCALE' && fromScoreInfo.fromGradeScale.gradeScaleType === 'NUMERIC') {
          if (numericSimilar == null) {
            if (fromScoreInfo.fromGradeScale.numericMax != null ||
                fromScoreInfo.fromGradeScale.numericMin != null) {
              numericSimilar = {
                max: <number>fromScoreInfo.fromGradeScale.numericMax,
                min: <number>fromScoreInfo.fromGradeScale.numericMin
              };
            } else {
              numericSimilar = false;
            }
          } else {
            if (fromScoreInfo.fromGradeScale.numericMax !== numericSimilar.max ||
                fromScoreInfo.fromGradeScale.numericMin !== numericSimilar.min) {
              numericSimilar = false;
            }
          }
        } else {
          numericSimilar = false;
        }
      }
    }

    let fromScoreTypes = Object.keys(types);

    let fromScoreType: 'POINTS' | 'ALL_SIMILAR_NUMERIC' | 'MIX' | undefined;
    if (fromScoreTypes.length === 1) {
      switch (fromScoreTypes[0]) {
        case 'POINTS':
          fromScoreType = 'POINTS';
          break;
        case 'PERCENT':
          fromScoreType = 'MIX';
          break;
        case 'GRADESCALE':
          fromScoreType = numericSimilar ? 'ALL_SIMILAR_NUMERIC' : 'MIX';
          break;
        default: //shut up tslint
      }
    } else {
      fromScoreType = 'MIX';
    }

    switch (fromScoreType) {
      case 'POINTS':
        rollUpPoints(fromScores, calcRelationship, toGradeScale, toScoreType, toScore, toMaxPoints);
        break;
      case 'MIX':
        rollUpMix(fromScores, calcRelationship, toGradeScale, toScoreType, toScore, toMaxPoints);
        break;
      case 'ALL_SIMILAR_NUMERIC':
        rollUpAllNumeric(fromScores, calcRelationship, toGradeScale, toScoreType, toScore, toMaxPoints);
        break;
      default: //shut up tslint
    }

    return toScore;
  }

  export function rollUpMix(fromScores: Array<{fromScore: ScoreRI,
                              fromGradeScale: GradeScaleRI,
                              fromScoreType: AssignmentScoreType,
                              fromMaxPoints: number,
                              fromWeight?: number}>,
                            calcRelationship: CalcRelationship,
                            toGradeScale: GradeScaleRI,
                            toScoreType: AssignmentScoreType,
                            toScore: ScoreRI,
                            toMaxPoints?: number): void {
    let numerator = 0;
    let denominator = 0;

    for (let fromScoreInfo of fromScores) {
      switch (fromScoreInfo.fromScoreType) {
        case 'POINTS':
          numerator += fromScoreInfo.fromScore.points / fromScoreInfo.fromMaxPoints;
          denominator += 1;
          break;
        case 'PERCENT':
          numerator += fromScoreInfo.fromScore.percent / 100;
          denominator += 1;
          break;
        case 'GRADESCALE':
          let percentString: string | undefined = GradeScaleUtil.convertGradeScaleItemToScoreOfType(getGrade(fromScoreInfo.fromScore, fromScoreInfo.fromGradeScale),
                                                                                                    fromScoreInfo.fromGradeScale,
                                                                                                    'PERCENT',
                                                                                                    false,
                                                                                                    calcRelationship);
          if (percentString != null) {
            let weight = fromScoreInfo.fromWeight;
            if (weight == null) {
              weight = 1;
            }
            numerator += Number(percentString) / 100 * weight;
            denominator += weight;
          }
          break;
        default: //shut up tslint
      }
    }

    let percent = numerator / denominator;

    switch (toScoreType) {
      case 'POINTS':
        toScore.points = NumberUtil.round(percent * toMaxPoints, 2);
        setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(toScore.points), toGradeScale, 'POINTS', true, calcRelationship, toMaxPoints), toScore, toGradeScale);
        break;
      case 'PERCENT':
        toScore.percent = NumberUtil.round(percent * 100, 2);
     //Fall through and set the gradeScale
      case 'GRADESCALE':
        setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(percent * 100), toGradeScale, 'PERCENT', true, calcRelationship, toMaxPoints), toScore, toGradeScale);
        break;
      default: //shut up tslint
    }

    if (toScoreType !== 'PERCENT') {
      toScore.percent = NumberUtil.round(percent * 100, PRECISION);
    }
  }

  export function rollUpPoints(fromScores: Array<{fromScore: ScoreRI,
                                 fromGradeScale: GradeScaleRI,
                                 fromScoreType: AssignmentScoreType,
                                 fromMaxPoints: number,
                                 fromWeight?: number}>,
                               calcRelationship: CalcRelationship,
                               toGradeScale: GradeScaleRI,
                               toScoreType: AssignmentScoreType,
                               toScore: ScoreRI,
                               toMaxPoints?: number
  ): void {

    let totals = fromScores.reduce((total, fromScoreInfo) => {
      return {
        numerator: fromScoreInfo.fromScore.points ? (fromScoreInfo.fromScore.points + (<any>total).numerator) : (<any>total).numerator,
        denominator: fromScoreInfo.fromMaxPoints + (<any>total).denominator
      };
    }, { numerator: 0, denominator: 0 });

    let totalsAsPercent: number = totals.numerator / totals.denominator;

    //In the case that the denominator has 0 points, set the total to 0.
    if (isNaN(totalsAsPercent)) {
      totalsAsPercent = 0;
    }

    switch (toScoreType) {
      case 'POINTS':
        if (totals.denominator === toMaxPoints) {
          toScore.points = NumberUtil.round(totals.numerator, 2);
        } else {
          toScore.points = NumberUtil.round(totalsAsPercent * toMaxPoints, 2);
        }

        setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(toScore.points), toGradeScale, 'POINTS', true, calcRelationship, toMaxPoints), toScore, toGradeScale);
        break;
      case 'PERCENT':
        toScore.percent = NumberUtil.round(totalsAsPercent * 100, 2);
     //Fall through and set the gradeScale
      case 'GRADESCALE':
        setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(totalsAsPercent * 100), toGradeScale, 'PERCENT', true, calcRelationship, 0), toScore, toGradeScale);
        break;
      default: //shut up tslint
    }

    if (toScoreType !== 'PERCENT') {
      toScore.percent = NumberUtil.round(totalsAsPercent * 100, PRECISION);
    }
  }

  export function rollUpAllNumeric(fromScores: Array<{
                                     fromScore: ScoreRI,
                                     fromGradeScale: GradeScaleRI,
                                     fromScoreType: AssignmentScoreType,
                                     fromMaxPoints: number,
                                     fromWeight?: number}>,
                                   calcRelationship: CalcRelationship,
                                   toGradeScale: GradeScaleRI,
                                   toScoreType: AssignmentScoreType,
                                   toScore: ScoreRI,
                                   toMaxPoints?: number): void {
    let totals = fromScores.reduce((total, fromScoreInfo) => {
      if (fromScoreInfo.fromScore.numericGrade) {
        let weight = fromScoreInfo.fromWeight != null ? fromScoreInfo.fromWeight : 1;
        return {
          numerator: fromScoreInfo.fromScore.numericGrade * weight + total.numerator,
          denominator:  total.denominator + weight
        };
      } else {
        return {
          numerator: total.numerator,
          denominator: total.denominator
        };
      }
    }, { numerator: 0, denominator: 0 });

    let numericValue = totals.numerator / totals.denominator;

    if (toGradeScale.gradeScaleType === 'NUMERIC' &&
     toGradeScale.numericMin === fromScores[0].fromGradeScale.numericMin &&
     toGradeScale.numericMax === fromScores[0].fromGradeScale.numericMax) {
      switch (toScoreType) {
        case 'POINTS':
          toScore.points = <number>GradeScaleUtil.convertNumericValueTo('POINTS', numericValue, toGradeScale, calcRelationship, true, toMaxPoints);
          break;
        case 'PERCENT':
          toScore.percent = <number>GradeScaleUtil.convertNumericValueTo('PERCENT', numericValue, toGradeScale, calcRelationship, true);
          break;
        default: //shut up tslint
      }
      setGrade(GradeScaleUtil.convertNumericValueTo('GRADESCALE', numericValue, toGradeScale, calcRelationship, true), toScore, toGradeScale);
    } else {
      let highestWeightFromScoreInfo;

      for (let fromScoreInfo of fromScores) {
        if (highestWeightFromScoreInfo == null || highestWeightFromScoreInfo.fromWeight < fromScoreInfo.fromWeight) {
          highestWeightFromScoreInfo = fromScoreInfo;
        }
      }

      let percent = <number>GradeScaleUtil.convertNumericValueTo('PERCENT', numericValue, highestWeightFromScoreInfo.fromGradeScale, calcRelationship, true, toMaxPoints);

      switch (toScoreType) {
        case 'POINTS':
          toScore.points = Number(GradeScaleUtil.convertNumericValueTo('POINTS', numericValue, highestWeightFromScoreInfo.fromGradeScale, calcRelationship, true, toMaxPoints));
          setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(toScore.points), toGradeScale, 'POINTS', true, calcRelationship, toMaxPoints), toScore, toGradeScale);
          break;
        case 'PERCENT':
          toScore.percent = NumberUtil.round(percent, 2);
          setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(percent), toGradeScale, 'PERCENT', true), toScore, toGradeScale);
          break;
        case 'GRADESCALE':
          setGrade(GradeScaleUtil.convertScoreOfTypeToGradeScaleItem(String(percent), toGradeScale, 'PERCENT', true), toScore, toGradeScale);
          break;
        default: //shut up tslint
      }

      if (toScoreType !== 'PERCENT') {
        toScore.percent = NumberUtil.round(percent, PRECISION);
      }

    }
  }

  export function generateScoreRi(typedScore: string, gradeScaleScore: string, scoreType: AssignmentScoreType, gradeScale: GradeScaleRI, calcRelationship: CalcRelationship, percent?: number, pointsPossible?: number): ScoreRI {
    let hasTypedScore = typedScore != null && typedScore.trim().length > 0;
    let hasGradeScaleScore = gradeScaleScore != null && gradeScaleScore.trim().length > 0;

    let score: ScoreRI = {
      actualScoreEntered: hasTypedScore ? typedScore : ''
    };

    if (hasTypedScore || hasGradeScaleScore) {
      setGrade(hasGradeScaleScore ? gradeScaleScore.trim() : undefined, score, gradeScale);

      let specialGradeScaleItem = GradeScaleUtil.getSpecialGradeScaleItem(typedScore, gradeScale, scoreType);
      score.actualScoreKind = specialGradeScaleItem != null ? 'SCORE_CODE' : 'REAL_SCORE';
      switch (scoreType) {
        case 'POINTS':
          if (specialGradeScaleItem != null) {
            let scoreAsString = GradeScaleUtil.convertSpecialGradeScaleItemTo(specialGradeScaleItem, gradeScale, scoreType, false, calcRelationship, pointsPossible);
            if (scoreAsString != null) {
              score.points = Number(scoreAsString);
            }
          } else {
            score.points = Number(typedScore);
          }
          score.percent = percent;
          break;

        case 'PERCENT':
        case 'GRADESCALE':
          score.percent = percent;
          if (percent != null && pointsPossible != null) {
            score.points = roundToPrecision(percent / 100 * pointsPossible, 2, PRECISION, true) ;
          }
          break;
        default: //shut up tslint
      }
    }
    return score;
  }

  export function hasNumericDecimals(gradeScale: GradeScaleRI): boolean {
    if (gradeScale != null && gradeScale.numericDecimals == null) {
      return false;
    }
    return gradeScale.numericDecimals > 0;
  }

  export function isBlank(score: ScoreRI): boolean {
    return score.points == null && score.percent == null && (score.letterGrade == null || score.letterGrade === '') && score.numericGrade == null;
  }
}
